import { createContext, useContext, useEffect, type ReactNode } from "react";

/**
 * § Pickers, ColorPicker, the Recent row (2026-09-30): the Team's own colours outside the palette,
 * most recently used first, read from the values the Team already holds rather than from a history
 * of its own. The picker lives in `packages/ui` and reads no route, so the application provides the
 * colours once around the Team's screens; a picker asks for them when it opens (`request`), so no
 * screen reads them until a person is choosing a colour.
 */
export interface RecentColors {
  /** The colours as the API stores them, `#rrggbb`, newest first. */
  readonly colors: readonly string[];
  /** Called by an open picker; the provider starts its reads then. */
  readonly request: () => void;
}

const NONE: RecentColors = { colors: [], request: () => undefined };

const RecentColorsContext = createContext<RecentColors>(NONE);

export function RecentColorsProvider({ value, children }: { readonly value: RecentColors; readonly children: ReactNode }) {
  return <RecentColorsContext.Provider value={value}>{children}</RecentColorsContext.Provider>;
}

/** The recent colours for an open picker, asking the provider to read them. */
export function useRecentColors(): readonly string[] {
  const { colors, request } = useContext(RecentColorsContext);
  useEffect(() => {
    request();
  }, [request]);
  return colors;
}
